import React, { useMemo, useEffect, useState } from "react";
import {
  ArticleCard,
  Loader,
  SocialArticleCard,
  Carousel,
  Modal,
} from "@relentless/rmds/v1/relentless.esm.js";
import { HeroBanner, SectionHeader } from "../components/SiteUI";
import { useBlogPosts, useInstagramPosts, useYouTubePosts } from "../lib/api";
import { withPublicUrl } from "../lib/publicUrl";
import "../App.scss";
import styles from "./SocialPage.module.scss";

const HERO_MESSAGES = [
  {
    title: "Keep going",
    subtitle: "Small steps still count",
    body: "Progress compounds when you keep showing up, especially on the days that test your discipline.",
    accent: "Stay relentless",
  },
  {
    title: "Do hard things",
    subtitle: "Growth lives in discomfort",
    body: "Lean into the work that stretches you. Confidence is built after the reps, not before them.",
    accent: "Push forward",
  },
  {
    title: "You are not behind",
    subtitle: "Your pace is still valid",
    body: "Keep building with intention. The right direction matters more than matching someone else's timeline.",
    accent: "Trust the process",
  },
];

export default function HomePage() {
  const { posts: blogPosts, loading: postsLoading } = useBlogPosts();
  const { posts: instagramPosts, loading: instagramLoading } =
    useInstagramPosts();
  const { posts: youTubePosts, loading: youTubeLoading } = useYouTubePosts();
  // TikTok on hold for now.
  // const { videos: tikTokVideos, loading: tikTokLoading } = useTikTokVideos();
  const [activeItem, setActiveItem] = useState(null);
  const [heroMessageIndex, setHeroMessageIndex] = useState(0);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setHeroMessageIndex((current) => (current + 1) % HERO_MESSAGES.length);
    }, 10000);

    return () => window.clearInterval(intervalId);
  }, []);

  const heroMessage = HERO_MESSAGES[heroMessageIndex];

  const latestPosts = useMemo(() => {
    const list = blogPosts || [];
    const toTime = (post) => {
      const raw = post?.publishedAt || post?.createdAt || "";
      const parsed = Date.parse(raw);
      return Number.isNaN(parsed) ? 0 : parsed;
    };
    return [...list].sort((a, b) => toTime(b) - toTime(a)).slice(0, 4);
  }, [blogPosts]);

  const whatsNew = useMemo(
    () =>
      (latestPosts || [])
        .map((p) => ({
          id: p.id,
          eyebrow: p.featured ? "Featured" : undefined,
          title: p.title,
          content: p.excerpt || "",
          image_url: p.imgeUrl,
          metaRows: [p.created, p.cat].filter((val) =>
            typeof val === "string" ? val.trim().length : Boolean(val),
          ),
          slug: p.slug,
          href: `/blog/${p.slug || p.id}`,
        }))
        .filter((p) => Boolean(p.title)),
    [latestPosts],
  );
  const formatMetaDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const isVideoUrl = (url) => {
    if (!url) return false;
    return /\.(mp4|mov|webm)(\?|#|$)/i.test(String(url));
  };

  const getVideoMimeType = (url) => {
    const normalized = String(url || "").toLowerCase();
    if (/\.webm(\?|#|$)/.test(normalized)) return "video/webm";
    if (/\.mov(\?|#|$)/.test(normalized)) return "video/quicktime";
    if (/\.mp4(\?|#|$)/.test(normalized)) return "video/mp4";
    return undefined;
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return "";
    try {
      const parsed = new URL(url);
      const host = parsed.hostname.replace(/^www\./, "");
      if (host === "youtu.be") {
        const id = parsed.pathname.replace(/^\//, "");
        return id ? `https://www.youtube.com/embed/${id}` : "";
      }
      if (host.endsWith("youtube.com")) {
        const id = parsed.searchParams.get("v");
        return id ? `https://www.youtube.com/embed/${id}` : "";
      }
      return "";
    } catch (_) {
      return "";
    }
  };

  const getInstagramEmbedUrl = (url) => {
    if (!url) return "";
    try {
      const parsed = new URL(url);
      const host = parsed.hostname.replace(/^www\./, "");
      if (!host.endsWith("instagram.com")) return "";
      const pathname = parsed.pathname.replace(/\/+$/, "");
      return pathname ? `https://www.instagram.com${pathname}/embed` : "";
    } catch (_) {
      return "";
    }
  };

  const openPreview = (item) => setActiveItem(item);
  const closePreview = () => setActiveItem(null);

  const instagramItems = useMemo(() => {
    const list = Array.isArray(instagramPosts) ? instagramPosts : [];
    return list
      .map((post, index) => {
        const caption = post?.caption || "";
        const title = post?.title || " ";
        const isVideo =
          String(post?.media_type || "").toUpperCase() === "VIDEO";
        const mediaUrl = post?.media_url || "";
        const thumbUrl = post?.thumbnail_url || "";
        const cardImage = isVideo ? thumbUrl || mediaUrl : mediaUrl || thumbUrl;
        const sortTime = post?.timestamp ? Date.parse(post.timestamp) : 0;
        return {
          id: `ig-${post?.id || index}`,
          type: "Instagram",
          title,
          body: caption,
          date: formatMetaDate(post?.timestamp),
          sortTime,
          image_url: cardImage,
          media_url: mediaUrl,
          media_type: post?.media_type || "",
          thumbnail_url: thumbUrl,
          link: post?.permalink || "",
          seed: `ig-${post?.id || index}`,
        };
      })
      .filter((item) => item.image_url);
  }, [instagramPosts]);

  const youTubeItems = useMemo(() => {
    const list = Array.isArray(youTubePosts) ? youTubePosts : [];
    return list
      .map((post, index) => {
        const title = post?.title || "YouTube video";
        const description = post?.description || "";
        const publishedAt = post?.video_published_at || "";
        const thumbUrl = post?.thumbnail_url || "";
        const videoUrl = post?.video_url || "";
        const sortTime = publishedAt ? Date.parse(publishedAt) : 0;
        return {
          id: `yt-${post?.id || index}`,
          type: "YouTube",
          title,
          body: description,
          date: formatMetaDate(publishedAt),
          sortTime,
          image_url: thumbUrl,
          media_url: videoUrl,
          thumbnail_url: thumbUrl,
          link: videoUrl,
          seed: post?.yt_id || `yt-${post?.id || index}`,
        };
      })
      .filter((item) => item.image_url || item.media_url);
  }, [youTubePosts]);

  /* const tikTokItems = useMemo(() => {
    const list = Array.isArray(tikTokVideos) ? tikTokVideos : [];
    return list
      .map((video, index) => {
        const caption = video?.caption || video?.title || "";
        const title = video?.title || caption || "TikTok video";
        const createdAt =
          video?.create_time || video?.created_at || video?.timestamp || "";
        const sortTime = createdAt ? Date.parse(createdAt) : 0;
        const imageUrl =
          video?.thumbnail_url ||
          video?.cover_image_url ||
          video?.cover_image ||
          video?.preview_url ||
          video?.image_url ||
          "";
        const mediaUrl =
          video?.video_url || video?.media_url || video?.url || "";
        return {
          id: video?.id || `tt-${index}`,
          type: "TikTok",
          title,
          body: caption,
          date: formatMetaDate(createdAt),
          sortTime,
          image_url: imageUrl,
          media_url: mediaUrl,
          link: video?.share_url || mediaUrl,
          seed: video?.id || `tt-${index}`,
        };
      })
      .filter((item) => item.image_url || item.media_url);
  }, [tikTokVideos]); */

  const recentContentItems = useMemo(() => {
    const latest = (items) =>
      [...items]
        .sort((a, b) => (b.sortTime || 0) - (a.sortTime || 0))
        .slice(0, 3);
    const combined = [
      ...latest(instagramItems),
      ...latest(youTubeItems),
      // ...latest(tikTokItems),
    ];
    return combined.sort((a, b) => (b.sortTime || 0) - (a.sortTime || 0));
  }, [instagramItems, youTubeItems]);

  if (postsLoading || instagramLoading || youTubeLoading) {
    return (
      <div className="rm-page-loading">
        <Loader />
      </div>
    );
  }

  return (
    <>
      {/* Home Hero */}
      <HeroBanner
        variant="home"
        images={[withPublicUrl("/assets/img/homeBanner.png")]}
        title={heroMessage.title}
        subtitle={heroMessage.subtitle}
        body={heroMessage.body}
        contentAlign="bottom-left"
        showNavigation={false}
        parallax
        layer0={[
          {
            type: "dp",
            className: "dp left dp--thick",
          },
          {
            type: "dp",
            className: "dp left2 dp--thick",
          },
        ]}
        layer1={[
          {
            type: "text",
            text: heroMessage.accent,
          },
          {
            type: "dp",
            className: "dp left dp--thick",
          },
        ]}
        layer2={[
          {
            type: "image",
            src: withPublicUrl("/assets/img/homeBanner1.png"),
            alt: "",
          },
        ]}
      />

      <section className="homeContent">
        {/* What's New / Latest Blog Posts */}
        <section
          className="recentBlog stories rm-page-section flex flex-col"
          aria-labelledby="whats-new-header"
          id="whats-new"
        >
          <SectionHeader
            id="whats-new-header"
            eyebrow="Latest"
            title={"What’s New"}
          />
          <div className="whats-new-grid items-center secBody">
            {postsLoading && !whatsNew.length
              ? Array.from({ length: 4 }, (_, i) => (
                  <Loader key={`whats-new-loader-${i}`} />
                ))
              : whatsNew.map((a) => (
                  <ArticleCard
                    key={`${a.id}`}
                    variant="a_card"
                    imageTop={true}
                    eyebrow={a.eyebrow}
                    title={a.title}
                    content={a.content}
                    imageUrl={a.image_url}
                    metaRows={a.metaRows}
                    ctaLabel="Read more"
                    ctaProps={{
                      href: a.href || `/blog/${a.slug || a.id}`,
                      link: true,
                    }}
                  />
                ))}
          </div>
        </section>

        {/* Recent Social Content Carousel */}
        <section
          className="recentContent stories rm-page-section flex flex-col"
          aria-labelledby="recent-content-header"
          id="recent-content"
        >
          <SectionHeader
            id="recent-content-header"
            eyebrow="Social"
            uppercase
            title={"Recent Content"}
            actionButton={{ label: "View all", link: true, href: "/social" }}
          />
          {recentContentItems.length ? (
            <Carousel
              className="recent-content-row secBody"
              items={recentContentItems}
              renderItem={(item) => (
                <div
                  className="rm-carousel__slide"
                  key={`recent-${item.id}`}
                  onClick={() => openPreview(item)}
                >
                  <SocialArticleCard
                    item={item}
                    isVideoUrl={isVideoUrl}
                    onClick={() => openPreview(item)}
                  />
                </div>
              )}
            />
          ) : (
            <Loader />
          )}
        </section>
      </section>

      {/* Social Content Preview Modal */}
      <Modal
        open={Boolean(activeItem)}
        onClose={closePreview}
        title={activeItem?.title || "Recent content"}
      >
        {activeItem ? (
          <div className={styles.mediaModal}>
            {String(activeItem.type || "").toLowerCase() === "youtube" ? (
              <div className={styles.youtubeEmbed}>
                <iframe
                  className={styles.youtubeFrame}
                  title={activeItem.title || "YouTube video"}
                  src={getYouTubeEmbedUrl(activeItem.media_url)}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : String(activeItem.type || "").toLowerCase() === "instagram" &&
              activeItem.media_type === "VIDEO" &&
              getInstagramEmbedUrl(activeItem.link) ? (
              <div className={styles.youtubeEmbed}>
                <iframe
                  className={styles.youtubeFrame}
                  title={activeItem.title || "Instagram video"}
                  src={getInstagramEmbedUrl(activeItem.link)}
                  allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : activeItem.media_type === "VIDEO" ||
              isVideoUrl(activeItem.media_url) ? (
              activeItem.media_url ? (
                <video
                  key={activeItem.media_url}
                  className={styles.mediaFrame}
                  controls
                  playsInline
                  preload="metadata"
                  poster={
                    activeItem.thumbnail_url || activeItem.image_url || ""
                  }
                >
                  <source
                    src={activeItem.media_url}
                    type={getVideoMimeType(activeItem.media_url)}
                  />
                </video>
              ) : (
                <p className={styles.mediaCaption}>Video unavailable.</p>
              )
            ) : (
              <img
                className={styles.mediaFrame}
                src={activeItem.media_url || activeItem.image_url || ""}
                alt={activeItem.title || "Social media"}
                loading="lazy"
              />
            )}
            {activeItem.body ? (
              <p className={styles.mediaCaption}>{activeItem.body}</p>
            ) : null}
          </div>
        ) : null}
      </Modal>

      {/* Home Page Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Person",
            name: "Tom Alvarez",
            url: "https://www.therelentlessminds.com/",
            jobTitle: "Product Design Leader",
            worksFor: {
              "@type": "Organization",
              name: "Relentless Minds",
            },
            sameAs: [
              "https://www.youtube.com/@RelentlessMinds",
              "https://www.tiktok.com/@relentlessminds",
            ],
          }),
        }}
      />
    </>
  );
}
